import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Group, Heading, Text } from '@optiaxiom/react';
import { IconCheckDouble } from '@optimizely/axiom-icons';
import type { ExpandedState, RowSelectionState } from '@tanstack/react-table';

import { useNamespacedNavigate } from '@frontend/hooks';

import {
  useAcknowledgeBadge,
  useNotificationActions,
  useNotificationFeatures,
  useNotificationsUrlParams,
  useOffsetNotificationList,
  useUnreadCount,
} from '../hooks';
import type { NotificationRow } from '../types';
import { getDeepLinkPath } from '../utils';

import { NotificationTable } from './table-view/NotificationTable';
import { NotificationBulkActionsBar } from './NotificationBulkActionsBar';
import { NotificationFilterBar } from './NotificationFilterBar';

export function NotificationCenterPage() {
  const navigate = useNamespacedNavigate();
  const { badgeEnabled, groupingEnabled } = useNotificationFeatures();
  const { mutate: markSeen } = useAcknowledgeBadge();
  const { params, updateParams } = useNotificationsUrlParams();

  useEffect(() => {
    if (!badgeEnabled) return;
    markSeen();
    return () => markSeen();
  }, [markSeen, badgeEnabled]);
  const { page, pageSize, status, agentId, category, priority, time, view } = params;

  const { data, isPending } = useOffsetNotificationList({
    page,
    pageSize,
    status,
    agentId,
    category,
    priority,
    time,
    grouped: groupingEnabled,
  });
  const { data: unreadCount } = useUnreadCount({ grouped: groupingEnabled });
  const actions = useNotificationActions();

  const rows = useMemo(() => data?.rows ?? [], [data?.rows]);
  const pagination = data?.pagination;

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});

  useEffect(() => {
    setRowSelection({});
    setExpanded({});
  }, [page, view]);

  const selectedIds = useMemo(() => {
    const selected = Object.keys(rowSelection).filter(k => rowSelection[k]);
    const leafIds = new Set(
      rows.flatMap(r => (r.notification ? [r.id] : r.subRows.map(sr => sr.id)))
    );
    return selected.filter(id => leafIds.has(id));
  }, [rowSelection, rows]);

  const handleMarkRead = useCallback(
    (id: string) => actions.markAsRead.mutate(id),
    [actions.markAsRead]
  );
  const handleMarkUnread = useCallback(
    (id: string) => actions.markAsUnread.mutate(id),
    [actions.markAsUnread]
  );
  const handleDelete = useCallback(
    (id: string) => actions.deleteNotification.mutate(id),
    [actions.deleteNotification]
  );

  const handleGroupMarkRead = useCallback(
    (groupId: string) => actions.bulkMarkAsRead.mutate({ group_id: groupId }),
    [actions.bulkMarkAsRead]
  );
  const handleGroupDelete = useCallback(
    (groupId: string) => actions.bulkDelete.mutate({ group_id: groupId }),
    [actions.bulkDelete]
  );

  const handleBulkRead = useCallback(() => {
    actions.bulkMarkAsRead.mutate({ ids: selectedIds });
    setRowSelection({});
  }, [actions.bulkMarkAsRead, selectedIds]);

  const handleBulkUnread = useCallback(() => {
    actions.bulkMarkAsUnread.mutate({ ids: selectedIds });
    setRowSelection({});
  }, [actions.bulkMarkAsUnread, selectedIds]);

  const handleBulkDismiss = useCallback(() => {
    actions.bulkDelete.mutate({ ids: selectedIds });
    setRowSelection({});
  }, [actions.bulkDelete, selectedIds]);

  const handleActivate = useCallback(
    (row: NotificationRow) => {
      const notification = row.notification;
      if (!notification) return;
      if (!notification.is_read) {
        actions.markAsRead.mutate(notification.id);
      }
      navigate(getDeepLinkPath(notification));
    },
    [actions.markAsRead, navigate]
  );

  const handleMarkAllRead = useCallback(() => {
    actions.bulkMarkAsRead.mutate(undefined);
  }, [actions.bulkMarkAsRead]);

  return (
    <Group flexDirection="column" gap="16" px="32" py="24">
      <Group gap="12" alignItems="center">
        <Heading color="fg.default" level="3">
          Notification Center
        </Heading>
        {unreadCount != null && unreadCount > 0 && (
          <Badge intent="information">{unreadCount} unread</Badge>
        )}
      </Group>

      <NotificationFilterBar
        status={status}
        agentId={agentId}
        category={category}
        priority={priority}
        time={time}
        view={view}
        onStatusChange={s => updateParams({ status: s })}
        onAgentChange={a => updateParams({ agentId: a })}
        onCategoryChange={c => updateParams({ category: c })}
        onPriorityChange={p => updateParams({ priority: p })}
        onTimeChange={t => updateParams({ time: t })}
        onViewChange={v => updateParams({ view: v })}
        actions={
          <Button
            size="md"
            appearance="default"
            icon={<IconCheckDouble />}
            onClick={handleMarkAllRead}
          >
            Mark all as read
          </Button>
        }
      />

      {selectedIds.length > 0 && (
        <NotificationBulkActionsBar
          selectedCount={selectedIds.length}
          onMarkRead={handleBulkRead}
          onMarkUnread={handleBulkUnread}
          onDismiss={handleBulkDismiss}
        />
      )}

      {view === 'table' && (
        <NotificationTable
          rows={rows}
          pagination={pagination}
          isLoading={isPending}
          page={page}
          pageSize={pageSize}
          onPageChange={p => updateParams({ page: p })}
          onPageSizeChange={ps => updateParams({ pageSize: ps })}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          expanded={expanded}
          onExpandedChange={setExpanded}
          onActivate={handleActivate}
          onMarkRead={handleMarkRead}
          onMarkUnread={handleMarkUnread}
          onDelete={handleDelete}
          onGroupMarkRead={handleGroupMarkRead}
          onGroupDelete={handleGroupDelete}
        />
      )}

      {!isPending && rows.length === 0 && view === 'table' && (
        <Group justifyContent="center" py="48">
          <Text color="fg.tertiary">No notifications</Text>
        </Group>
      )}
    </Group>
  );
}
